import React, { useEffect, useState } from "react";
import { loadStripe } from "@stripe/stripe-js";
import { useSelector, useDispatch } from "react-redux";
import { returnInTZ } from '../utils/timezone';
import {
  Elements,
  useStripe,
  useElements,
  CardElement,
} from "@stripe/react-stripe-js";
import {
    Flex,
    Heading,
    Button,
    Stack,
    Alert,
    AlertIcon,
    Text,
    Box,
    Spinner,
    useColorMode,
    Link,
    Divider,
    Center,
} from "@chakra-ui/react";
import { createPaymentIntent, confirmPayment, createPaymentIntentExpired, confirmPaymentExpired } from "../store/payments/thunks";
import { getPreferences, getAccountValues } from "../store/user/thunks";
import { getTradesStats, getTradesPage } from "../store/trades/thunks";
import { TOKEN } from "../api/axiosInstance";
import { useNavigate, useLocation } from "react-router-dom";

import useToastNotification from "../hooks/useToastNotification";
import '../styles/profile.css';

// Load Stripe (Use your Stripe **Public** key here)
const env = process.env.REACT_APP_ENV;
let stripePublicKey;
if (env === "prod") {
  stripePublicKey = process.env.REACT_APP_STRIPE_PUB_KEY;
} else {
  stripePublicKey = process.env.REACT_APP_STRIPE_PUB_KEY_TEST;
}
const stripePromise = loadStripe(stripePublicKey);

// Card Input Styles
const cardStyle = {
    style: {
        base: {
            fontSize: "16px",
            color: "#32325d",
            "::placeholder": {
                color: "#aab7c4"
            }
        },
        invalid: {
            color: "#fa755a"
        }
    }
};

const cardStyleDark = {
    style: {
        base: {
            fontSize: "16px",
            color: "#ebf0f1",
            "::placeholder": {
                color: "#aab7c4"
            }
        },
        invalid: {
            color: "#fa755a"
        }
    }
};

const CheckoutForm = ({promptPayment, setPromptPayment, email}) => {
    const stripe = useStripe();
    const elements = useElements();
    const dispatch = useDispatch();
    const showToast = useToastNotification();
    const navigate = useNavigate();

    const { user } = useSelector((state) => state.user);
    const isLoggedIn = (((user && Object.keys(user).length > 2) || window.localStorage.getItem(TOKEN)) ? (true):(false));

    const [clientSecret, setClientSecret] = useState("");
    const [loading, setLoading] = useState(false);
    const [intentLoading, setIntentLoading] = useState(true);
    const [error, setError] = useState("");
    const { colorMode } = useColorMode();

    const today = new Date();

    const evaluateResponse = async (response) => {
        if (response?.result?.includes("Payment has already been completed for this Account") || response?.result?.includes("Payment Successful")) {
            if(isLoggedIn){
                showToast(response.result);
                await dispatch(getTradesStats());
                await dispatch(getPreferences());
                let filters = {};
                filters.date = returnInTZ(today.toISOString());
                await dispatch(getAccountValues({ filters }));
                filters = {};
                filters.page = 1;
                filters.numrows = 5;
                filters.trade_date = 'NULL';
                await dispatch(getTradesPage({ filters }));
                navigate("/");
            } else {
                showToast(response.result);
                setTimeout(() => {
                    navigate("/login");
                }, 500);
            }
        } else if (!response?.result?.includes("Payment Intent Created Successfully")) {
            showToast(response.result, { status: "error" });
        }
    }
    // Create payment intent on mount
    useEffect(() => {
        const fetchPaymentIntent = async () => {
            if(isLoggedIn) {
                try {
                    const response = await dispatch(createPaymentIntent());
                    const { payload } = response;
                    if (payload.clientSecret) {
                        setClientSecret(payload.clientSecret);
                    } else {
                        evaluateResponse(payload);
                        console.error("No clientSecret in response:", payload);
                    }
                } catch (err) {
                    showToast("Failed to create payment intent:", err, { status: "error" });
                    console.error("Failed to create payment intent:", err);
                    setError("Failed to prepare payment.");
                } finally {
                    setIntentLoading(false);
                }
            } else if (email !== "") {
                try {
                    const response = await dispatch(createPaymentIntentExpired({ email }));
                    const { payload } = response;
                    if (payload.clientSecret) {
                        setClientSecret(payload.clientSecret);
                    } else {
                        evaluateResponse(payload);
                        console.error("No clientSecret in response:", payload);
                    }
                } catch (err) {
                    showToast("Failed to create payment intent:", err, { status: "error" });
                    console.error("Failed to create payment intent:", err);
                    setError("Failed to prepare payment.");
                } finally {
                    setIntentLoading(false);
                }
            }
        };

        fetchPaymentIntent();
    }, []);

    const handleSubmit = async (e) => {
      e.preventDefault();
      setLoading(true);
      setError("");

      if (!stripe || !elements) {
        showToast("Stripe has not loaded", { status: "error" });
        setError("Stripe has not loaded.");
        setLoading(false);
        return;
      }

      const result = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: elements.getElement(CardElement),
        },
      });

      if (result.error) {
        showToast(result.error.message, { status: "error" });
        setError(result.error.message);
        setLoading(false);
      } else if (result.paymentIntent?.status === "succeeded") {
        if (isLoggedIn) {
            try {
                const payment_intent_id = result.paymentIntent.id
                const response = await dispatch(confirmPayment({ payment_intent_id }));
                const { payload } = response;
                evaluateResponse(payload);
            } catch (err) {
                showToast("Error confirming payment:", err, { status: "error" });
                console.error("Error confirming payment:", err);
                setError("Payment succeeded but could not update your account.");
            } finally {
                setLoading(false);
            }
        } else if (email !== "") {
            try {
                const payment_intent_id = result.paymentIntent.id
                const response = await dispatch(confirmPaymentExpired({ email, payment_intent_id }));
                const { payload } = response;
                evaluateResponse(payload);
            } catch (err) {
                showToast("Error confirming payment:", err, { status: "error" });
                console.error("Error confirming payment:", err);
                setError("Payment succeeded but could not update your account.");
            } finally {
                setLoading(false);
            }
        }
      }
    };

    const handleCancel = async (e) => {
        e.preventDefault();
        setPromptPayment(false);
        navigate("/");
    }

    return (
        <Flex
            direction="column"
            minH="100vh"
            bg={colorMode === 'light' ? "gray.200" : "gray.800"}
            justifyContent="center"
            alignItems="center"
        >
            <Stack
                flexDir="column"
                mb="2"
                justifyContent="center"
                alignItems="center"
            >
                <Box minW={{ base: "90%", md: "468px" }} rounded="lg" overflow="hidden" boxShadow="2px 4px 4px rgba(0,0,0,0.2)">
                    {intentLoading ? (
                        <Stack
                            spacing={4}
                            p="1rem"
                            backgroundColor={colorMode === 'light' ? "whiteAlpha.900" : "whiteAlpha.100"}
                            boxShadow="md"
                        >
                            <Center>
                                <Spinner
                                    thickness='4px'
                                    speed='0.65s'
                                    emptyColor='gray.200'
                                    color='blue.500'
                                    size='xl'
                                />
                            </Center>
                        </Stack>
                    ) : (
                        <form>
                            <Stack
                                spacing={4}
                                p="1rem"
                                backgroundColor={colorMode === 'light' ? "whiteAlpha.900" : "whiteAlpha.100"}
                                boxShadow="md"
                                minW='300px'
                            >
                            <Heading className="page-heading" fontSize="xl" ml={1} mb={-1} color={colorMode === 'light' ? 'gray.600' : 'gray.200'}>
                                {isLoggedIn ? "Checkout" : "Payment Required"}
                            </Heading>
                            {isLoggedIn ? (
                                <Text fontSize="sm" maxW="550px" paddingBottom={2}>
                                    By completing your purchase, you agree to our&nbsp;
                                    <Link href="/legal/MyTradingTracker_Terms_and_Conditions.pdf" isExternal textDecoration="underline">Terms and Conditions</Link> and&nbsp;
                                    <Link href="/legal/MyTradingTracker_Privacy_Policy.pdf" isExternal textDecoration="underline">Privacy Policy</Link>
                                </Text>
                            ) : (
                                <>
                                <Text maxW="500px">
                                    Your Free Trial has expired!
                                    <br/><br/>
                                    To continue using MyTradingTracker please complete payment below for a <b>$5 One-Time Charge</b> to upgrade your membership.
                                </Text>
                                <Text fontSize="sm" maxW="550px" paddingTop={2} paddingBottom={2}>
                                    By completing your purchase, you agree to our&nbsp;
                                    <Link href="/legal/MyTradingTracker_Terms_and_Conditions.pdf" isExternal textDecoration="underline">Terms and Conditions</Link> and&nbsp;
                                    <Link href="/legal/MyTradingTracker_Privacy_Policy.pdf" isExternal textDecoration="underline">Privacy Policy</Link>
                                </Text>
                                <Divider borderColor='gray.200' />
                                </>
                            )}
                            <CardElement options={colorMode === 'light' ? cardStyle : cardStyleDark}/>

                            <Button
                                borderRadius="md"
                                type="submit"
                                variant="solid"
                                colorScheme="blue"
                                width="full"
                                isDisabled={!stripe || !clientSecret || loading}
                                isLoading={loading}
                                loadingText="Processing..."
                                onClick={handleSubmit}
                            >
                                Pay $5
                            </Button>
                            <Button
                                borderRadius="md"
                                type="button"
                                variant="outline"
                                colorScheme="blue"
                                width="full"
                                onClick={handleCancel}
                            >
                                Cancel
                            </Button>
                            {error && (
                                <Alert status="error" borderRadius="md" fontSize="sm">
                                    <AlertIcon />
                                    {error}
                                </Alert>
                            )}
                            </Stack>
                        </form>
                    )}
                </Box>
            </Stack>
        </Flex>
    );
};

// Payment Page Wrapper (loads Stripe)
const Payment = ({promptPayment, setPromptPayment}) => {
    const location = useLocation();
    // Extract email from navigation state. Provide a fallback value if not available.
    const email = location.state?.email || "";

    return (
        <Elements stripe={stripePromise}>
        <CheckoutForm
            promptPayment={promptPayment}
            setPromptPayment={setPromptPayment}
            email={email}  // Passing the email down as a prop
        />
        </Elements>
    );
  };


export default Payment;